import { ProcessedTelemetryData } from '../telemetry/processed/ProcessedTelemetryData'
import { RawTelemetryData } from '../telemetry/raw/RawTelemetryData'
import {
  IndexedTelemetry,
  createIndexedTelemetry,
  findPointAtDistance,
  findPointAtElapsedTime,
  getPointsInDistanceRange,
  getPointsInTimeRange,
} from '../telemetry/indexing/IndexedTelemetry'
import { GpsPoint } from '../telemetry/processed/GpsPoint'
import { LapBounds } from './LapBounds'
import { LapMetadata } from './LapMetadata'
import { Device, Weather, WeatherCondition } from '../../api/apiTypes'
import { GyroscopeReading } from '../telemetry/raw/GyroscopeReading'
import { AccelerometerReading } from '../telemetry/raw/AccelerometerReading'
import { formatTimeSpan } from '../../utils/timeUtils'
import { SpatialBucket } from '../telemetry/spatial/SpatialBucket'

export class Lap {
  readonly id: string
  readonly lapNumber: number
  readonly driverName: string
  readonly sessionId: string
  readonly trackId: string
  readonly vehicleId?: string
  readonly vehicleName?: string
  readonly bounds: LapBounds
  readonly lapTime: number
  readonly processedTelemetryData: ProcessedTelemetryData
  readonly rawTelemetry: RawTelemetryData
  readonly indexedGps: IndexedTelemetry<GpsPoint>
  readonly spatialBuckets: SpatialBucket[]
  readonly sessionName: string
  readonly lapGroupingName: string
  readonly device: Device
  readonly gyro?: GyroscopeReading[]
  readonly accl?: AccelerometerReading[]
  readonly hasGyroscope: boolean
  readonly hasAccelerometer: boolean
  readonly weather: Weather
  isSelected: boolean = false
  isShared: boolean = false
  breakingPoints: GpsPoint[] = []
  slowestPoints: GpsPoint[] = []
  topSpeed: number = 0
  readonly lapStartDistance: number
  readonly lapFinishDistance: number

  constructor(
    metadata: LapMetadata,
    bounds: LapBounds,
    processedTelemetryData: ProcessedTelemetryData,
    rawTelemetry: RawTelemetryData,
    options?: {
      device?: Device
      sessionName?: string
      gyro?: GyroscopeReading[]
      accl?: AccelerometerReading[]
      hasGyroscope?: boolean
      hasAccelerometer?: boolean
      weather?: Weather
      spatialBuckets?: SpatialBucket[]
    }
  ) {
    this.id = metadata.lapId
    this.lapNumber = metadata.lapNumber
    this.driverName = metadata.driverName
    this.sessionId = metadata.trackSessionId
    this.trackId = metadata.trackId
    this.vehicleId = metadata.vehicleId
    this.vehicleName = metadata.vehicleName
    this.bounds = bounds
    this.lapTime = bounds.finishTime.getTime() - bounds.startTime.getTime()
    this.processedTelemetryData = processedTelemetryData
    this.rawTelemetry = rawTelemetry
    this.indexedGps = createIndexedTelemetry(processedTelemetryData.gpsPoints)
    this.spatialBuckets = options?.spatialBuckets ?? []
    this.lapStartDistance = bounds.startDistanceMeters
    this.lapFinishDistance = bounds.finishDistanceMeters
    this.sessionName = options?.sessionName ?? metadata.trackSessionId
    this.lapGroupingName = this.sessionName
    this.device = options?.device ?? { name: 'Unknown' }
    this.gyro = options?.gyro
    this.accl = options?.accl
    this.hasGyroscope =
      options?.hasGyroscope ?? (options?.gyro?.length ?? 0) > 0
    this.hasAccelerometer =
      options?.hasAccelerometer ?? (options?.accl?.length ?? 0) > 0
    this.weather = options?.weather ?? { condition: WeatherCondition.Dry }
  }

  getGpsPointAtDistance(distanceMeters: number): GpsPoint | null {
    const relativeDistance = distanceMeters - this.bounds.startDistanceMeters
    return findPointAtDistance(this.indexedGps, relativeDistance)
  }

  getGpsPointAtElapsedTime(elapsedMs: number): GpsPoint | null {
    return findPointAtElapsedTime(this.indexedGps, elapsedMs)
  }

  getGpsPoints(): GpsPoint[] {
    return this.processedTelemetryData.gpsPoints
  }

  get hasReadings(): boolean {
    const { gps, gyroscope, accelerometer } = this.rawTelemetry
    return (
      gps.length > 0 &&
      (!this.hasGyroscope || (gyroscope?.length ?? 0) > 0) &&
      (!this.hasAccelerometer || (accelerometer?.length ?? 0) > 0)
    )
  }

  get startTime(): Date {
    return this.bounds.startTime
  }

  get finishTime(): Date {
    return this.bounds.finishTime
  }

  getTelemetryInDistanceRange(
    startMeters: number,
    endMeters: number
  ): GpsPoint[] {
    return getPointsInDistanceRange(this.indexedGps, startMeters, endMeters)
  }

  getTelemetryInTimeRange(startMs: number, endMs: number): GpsPoint[] {
    return getPointsInTimeRange(this.indexedGps, startMs, endMs)
  }

  getMaxSpeed(): number {
    return this.processedTelemetryData.statistics.maxSpeedKmh
  }

  getAverageSpeed(): number {
    return this.processedTelemetryData.statistics.avgSpeedKmh
  }

  getLapDistance(): number {
    return this.bounds.finishDistanceMeters - this.bounds.startDistanceMeters
  }

  getMaxLeanAngle(): number | undefined {
    return this.processedTelemetryData.statistics.maxLeanAngle
  }

  getMaxCombinedG(): number | undefined {
    return this.processedTelemetryData.statistics.maxCombinedG
  }

  static msToTimeString(s: number) {
    return (
      formatTimeSpan(s, {
        granularity: 'tenths',
        format: 'mm:ss.S',
        decimals: 3,
      }) ?? ''
    )
  }

  getBreakingPointsAsGps(): GpsPoint[] {
    return this.breakingPoints
  }

  getSlowestPointsAsGps(): GpsPoint[] {
    return this.slowestPoints
  }
}
